import React, { useState } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  title,
  children,
  onClose
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-charcoal/70 backdrop-blur-sm" onClick={onClose} data-id="element-57">
      <motion.div initial={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.95,
      y: 10
    }} transition={{
      duration: 0.2
    }} className="bg-offwhite rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-58">
        <div className="sticky top-0 bg-offwhite border-b border-forest/10 p-6 flex items-center justify-between rounded-t-2xl" data-id="element-59">
          <h2 className="text-2xl font-serif font-bold text-forest" data-id="element-60">{title}</h2>
          <button onClick={onClose} className="text-charcoal/50 hover:text-charcoal transition-colors p-1" aria-label="Close" data-id="element-61">
            <X size={24} data-id="element-62" />
          </button>
        </div>
        <div className="p-6 text-charcoal/80 text-sm leading-relaxed space-y-4" data-id="element-63">
          {children}
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-white/60 py-12 px-6 lg:px-8 text-center" data-id="element-64">
        <div className="max-w-4xl mx-auto" data-id="element-65">
          <h3 className="text-white font-serif text-2xl mb-6" data-id="element-66">
            Nutri-Sync Protocol
          </h3>
          <p className="text-sm mb-8 max-w-md mx-auto" data-id="element-67">
            Nutritional optimization for sustained energy and mental clarity.
          </p>
          <div className="flex justify-center gap-6 text-sm mb-8" data-id="element-68">
            <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors" data-id="element-69">
              Terms & Conditions
            </button>
            <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors" data-id="element-70">
              Privacy Policy
            </button>
          </div>
          <p className="text-xs text-white/40" data-id="element-71">
            © {new Date().getFullYear()} Nutri-Sync. All rights reserved. This
            product is not intended to diagnose, treat, cure, or prevent any
            disease.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-72">
        {activeModal === 'terms' && <Modal title="Terms & Conditions" onClose={() => setActiveModal(null)} data-id="element-73">
            <p data-id="element-74">
              <strong data-id="element-75">Last updated:</strong> April 2026
            </p>
            <p data-id="element-76">
              By purchasing and accessing the Nutri-Sync Protocol digital guide,
              you agree to the following terms and conditions.
            </p>
            <p data-id="element-77">
              <strong data-id="element-78">1. Digital Product.</strong> The Nutri-Sync Protocol is a
              digital information product delivered as a downloadable PDF. It is
              not a physical product, medical device, or pharmaceutical.
            </p>
            <p data-id="element-79">
              <strong data-id="element-80">2. Educational Purpose Only.</strong> All content within
              the guide is for informational and educational purposes only. It
              does not constitute medical advice, diagnosis, or treatment.
              Always consult a qualified healthcare professional before making
              changes to your diet or supplementation routine.
            </p>
            <p data-id="element-81">
              <strong data-id="element-82">3. No Refund Policy.</strong> Due to the digital nature of
              this product, all sales are final. By completing your purchase,
              you acknowledge that you are buying instant access to downloadable
              content.
            </p>
            <p data-id="element-83">
              <strong data-id="element-84">4. Intellectual Property.</strong> All content, text,
              graphics, and materials within the Nutri-Sync Protocol are
              protected by copyright. You may not reproduce, distribute, or
              resell any part of this product without prior written consent.
            </p>
            <p data-id="element-85">
              <strong data-id="element-86">5. Limitation of Liability.</strong> Nutri-Sync and its
              creators shall not be held liable for any outcomes, health
              changes, or damages resulting from the use of information provided
              in this guide.
            </p>
            <p data-id="element-87">
              <strong data-id="element-88">6. Modifications.</strong> We reserve the right to update
              these terms at any time. Continued use of the product constitutes
              acceptance of any changes.
            </p>
          </Modal>}

        {activeModal === 'privacy' && <Modal title="Privacy Policy" onClose={() => setActiveModal(null)} data-id="element-89">
            <p data-id="element-90">
              <strong data-id="element-91">Last updated:</strong> April 2026
            </p>
            <p data-id="element-92">
              Your privacy is important to us. This policy explains how we
              collect, use, and protect your information when you purchase or
              interact with the Nutri-Sync Protocol.
            </p>
            <p data-id="element-93">
              <strong data-id="element-94">1. Information We Collect.</strong> When you make a
              purchase, we collect your name, email address, and payment
              information. Payment processing is handled by secure third-party
              providers; we do not store your credit card details.
            </p>
            <p data-id="element-95">
              <strong data-id="element-96">2. How We Use Your Information.</strong> We use your
              information solely to deliver your purchased product, send order
              confirmations, and provide customer support if needed.
            </p>
            <p data-id="element-97">
              <strong data-id="element-98">3. No Sharing of Data.</strong> We do not sell, rent, or
              share your personal information with third parties for marketing
              purposes.
            </p>
            <p data-id="element-99">
              <strong data-id="element-100">4. Cookies.</strong> Our website may use essential cookies
              to ensure proper functionality. We do not use tracking cookies for
              advertising.
            </p>
            <p data-id="element-101">
              <strong data-id="element-102">5. Data Security.</strong> We implement industry-standard
              security measures to protect your personal information from
              unauthorized access, alteration, or disclosure.
            </p>
            <p data-id="element-103">
              <strong data-id="element-104">6. Your Rights.</strong> You may request access to,
              correction of, or deletion of your personal data at any time by
              reaching out through our website.
            </p>
            <p data-id="element-105">
              <strong data-id="element-106">7. Changes to This Policy.</strong> We may update this
              privacy policy periodically. Any changes will be reflected on this
              page with an updated date.
            </p>
          </Modal>}
      </AnimatePresence>
    </>;
}